import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/lib/auth";
import { openRazorpay } from "@/lib/razorpay";
import { FileText, Plus, TrendingUp, Download, Sparkles, Lock } from "lucide-react";

export default function AgentDashboard() {
  const { user, refreshMe } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [me, setMe] = useState(user);

  useEffect(() => {
    api.get("/quotations").then(({ data }) => setQuotes(data));
    api.get("/auth/me").then(({ data }) => setMe(data));
  }, []);

  const totalDownloads = quotes.reduce((s, q) => s + (q.pdf_downloads || 0), 0);
  const drafts = quotes.filter((q) => q.status === "draft").length;

  const daysToExpiry = (() => {
    if (!me?.subscription_expiry) return null;
    const ms = new Date(me.subscription_expiry) - new Date();
    return Math.max(0, Math.ceil(ms / 86400000));
  })();

  const renewNow = async () => {
    const pkg = me?.last_package_id || "pro";
    await openRazorpay({
      package_id: pkg,
      intent: "renewal",
      onSuccess: async () => {
        const fresh = await refreshMe();
        setMe(fresh);
      },
    });
  };

  const showRenewCta = daysToExpiry !== null && daysToExpiry <= 30;

  return (
    <div className="flex min-h-screen">
      <Sidebar role="agent" />
      <main className="flex-1 bg-[#FCFCFC] p-8 lg:p-12">
        <div className="max-w-6xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="overline">Studio</div>
              <h1 className="font-editorial text-4xl mt-2 text-slate-900">Welcome, {me?.agent_name || "Traveler"}.</h1>
              <p className="text-sm text-slate-500 mt-2">
                {me?.agency_name} {me?.agent_id && <> • <span className="font-mono-e text-xs">{me.agent_id}</span></>}
              </p>
            </div>
            <Link to="/quotations/templates" data-testid="dash-new-quotation-btn" className="btn-bronze inline-flex items-center gap-2">
              <Plus size={15} /> New Quotation
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            <StatCard label="Total Quotations" value={quotes.length} icon={FileText} testid="stat-total" />
            <StatCard label="Drafts" value={drafts} icon={Sparkles} testid="stat-drafts" />
            <StatCard label="PDF Downloads" value={totalDownloads} icon={Download} testid="stat-downloads" />
            <StatCard label="Conversion" value={quotes.length ? `${Math.round((totalDownloads/Math.max(quotes.length,1))*100)}%` : "—"} icon={TrendingUp} testid="stat-conv" />
          </div>

          {showRenewCta && (
            <div data-testid="renew-cta-card" className="mt-8 rounded overflow-hidden border border-amber-300 bg-amber-50 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded bg-amber-100 flex items-center justify-center shrink-0">
                  <AlertCircle size={20} className="text-amber-700" />
                </div>
                <div>
                  <div className="font-editorial text-xl text-slate-900">
                    {daysToExpiry === 0 ? "Your subscription has expired" : `Your subscription ends in ${daysToExpiry} day${daysToExpiry === 1 ? "" : "s"}`}
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    Renew now to keep generating quotations and vouchers without interruption.
                  </div>
                </div>
              </div>
              <button
                data-testid="renew-now-btn"
                onClick={renewNow}
                className="inline-flex items-center gap-2 bg-[#16A34A] hover:bg-emerald-700 text-white px-5 py-2.5 rounded text-sm font-semibold shrink-0"
              >
                <Lock size={14} /> Renew now
              </button>
            </div>
          )}

          <div className="mt-10 bg-white border border-border rounded">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="font-editorial text-xl">Recent quotations</div>
              <Link to="/quotations" data-testid="see-all-quotes-link" className="text-xs text-[#16A34A] uppercase tracking-widest">See all →</Link>
            </div>
            {quotes.length === 0 ? (
              <div className="p-14 text-center">
                <div className="font-editorial text-2xl">No quotations yet</div>
                <p className="text-sm text-slate-500 mt-2">Craft your first client quote in under 5 minutes.</p>
                <Link to="/quotations/new" data-testid="empty-state-cta" className="btn-bronze inline-flex items-center gap-2 mt-6"><Plus size={14} /> Create quotation</Link>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left px-6 py-3">Number</th><th className="text-left px-6 py-3">Title</th><th className="text-left px-6 py-3">Destination</th><th className="text-left px-6 py-3">Lead</th><th className="text-left px-6 py-3">Status</th><th className="px-6 py-3"></th></tr>
                </thead>
                <tbody>
                  {quotes.slice(0, 6).map((q) => (
                    <tr key={q.id} className="border-t border-border hover:bg-slate-50">
                      <td className="px-6 py-3 font-mono-e text-xs">{q.quotation_number}</td>
                      <td className="px-6 py-3">{q.package_title || "—"}</td>
                      <td className="px-6 py-3">{q.destination?.country || "—"}</td>
                      <td className="px-6 py-3">{q.lead?.name || "—"}</td>
                      <td className="px-6 py-3"><span className={`text-xs px-2 py-0.5 rounded ${q.status === "draft" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>{q.status}</span></td>
                      <td className="px-6 py-3 text-right"><Link to={`/quotations/${q.id}/edit`} className="text-[#16A34A] text-xs uppercase tracking-widest">Edit →</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-border rounded p-5">
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={16} className="text-[#16A34A]" />
      </div>
      <div className="font-editorial text-3xl mt-3">{value}</div>
    </div>
  );
}
